import { useEffect, useState } from "react";
import { getApp, getApps, initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { isServerSide } from "@jti/fe-common";
import { useConfig } from "../../providers/useConfig";
let messagingInstance = null;
let isOnMessageRegistered = false;
const requiredConfigKeys = [
    "firebaseAppId",
    "firebaseApiKey",
    "firebaseAuthDomain",
    "firebaseMessagingSenderId",
    "firebaseProjectId",
    "firebaseStorageBucket",
];
const getIsEnvReady = () => !isServerSide() && "navigator" in window;
const getIsConfigValid = (firebaseConfig) => requiredConfigKeys.every((key) => Boolean(firebaseConfig?.[key]));
const getFirebaseMessaging = (firebaseConfig, messagingKey) => {
    if (!getIsEnvReady()) {
        throw new Error("Firebase cannot be initiated on this environment");
    }
    if (!getIsConfigValid(firebaseConfig) || !messagingKey) {
        throw new Error("Firebase config is invalid");
    }
    if (!messagingInstance) {
        const app = getApps().length > 0
            ? getApp()
            : initializeApp({
                appId: firebaseConfig.firebaseAppId,
                apiKey: firebaseConfig.firebaseApiKey,
                authDomain: firebaseConfig.firebaseAuthDomain,
                messagingSenderId: firebaseConfig.firebaseMessagingSenderId,
                projectId: firebaseConfig.firebaseProjectId,
                storageBucket: firebaseConfig.firebaseStorageBucket,
            });
        messagingInstance = getMessaging(app);
    }
    return messagingInstance;
};
const registerOnMessageHandler = (messaging) => {
    if (isOnMessageRegistered) {
        return;
    }
    onMessage(messaging, (payload) => {
        const notificationTitle = payload?.notification?.title ?? payload?.data?.title ?? "Notification title";
        const notificationOptions = {
            body: payload?.notification?.body ?? payload?.data?.body ?? "",
            icon: payload?.notification?.image ?? payload?.data?.image,
        };
        new Notification(notificationTitle, notificationOptions);
    });
    isOnMessageRegistered = true;
};
export const useFirebaseNotifications = ({ onSuccess, onFail } = {}) => {
    const config = useConfig();
    const [token, setToken] = useState();
    const [status, setStatus] = useState(Notification.permission);
    const requestPermissionAndGetToken = async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                setStatus("denied");
                return;
            }
            const messaging = getFirebaseMessaging(config.firebaseSdkCredentials, config.firebaseVapidKey);
            const currentToken = await getToken(messaging, {
                vapidKey: config.firebaseVapidKey,
            });
            if (currentToken) {
                setToken(currentToken);
                registerOnMessageHandler(messaging);
                await onSuccess?.(currentToken);
                setStatus("granted");
            }
            else {
                setStatus("error");
                onFail?.();
            }
        }
        catch (error) {
            console.error(error);
            setStatus("error");
            onFail?.();
        }
    };
    useEffect(() => {
        if (!getIsEnvReady() || Notification.permission !== "granted") {
            return;
        }
        try {
            const messaging = getFirebaseMessaging(config.firebaseSdkCredentials, config.firebaseVapidKey);
            registerOnMessageHandler(messaging);
        }
        catch (error) {
            console.warn("Could not register onMessage handler", error);
        }
    }, [config]);
    return { token, status, requestPermissionAndGetToken };
};
//# sourceMappingURL=useFirebaseNotifications.js.map